import {StyleSheet} from "aphrodite";
import {useCallback, useRef, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {border, semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {useGuideAudioController, useSendChatMessage} from "@khan/guide-core";
import {GuideStateApi, useGuideState} from "@khan/guide-state";

import AskKhanmigoTextInput from "./ask-khanmigo-text-input.tsx";
import {getSuggestionsForPath, type Suggestion} from "./suggestions.ts";
import type {JsonPath} from "./types.ts";
import {useCoeditingAPI} from "./use-coediting-api.ts";
import {useCoeditingState} from "./use-coediting-state.ts";

/**
 * This is the component that appears within the popover when you have some
 * text selected in the coeditor. It renders a list of "suggestions": requests
 * or edits that you may want to ask of the LLM.
 */
export default function AskKhanmigoMenu(props: {path: JsonPath}) {
    const {path} = props;

    const {data} = useCoeditingState();
    const {requestNewValue} = useCoeditingAPI();
    const menuRef = useRef<HTMLDivElement>(null);
    const navigation = useGuideState("navigation");
    const threadId = useGuideState("threadId");
    const sendChatMessage = useSendChatMessage();
    const {isVoiceOn, selectedVoice} = useGuideAudioController();

    // If a suggestion is clicked that requires additional details (for
    // example, "Make changes to this", which requires the user to specify what
    // changes they want), we'll set this next bit of state to the suggestion
    // that we're editing:
    const [editingSuggestion, setEditingSuggestion] =
        useState<Suggestion | null>(null);

    const handleSuggestionAndClose = useCallback(
        ({kind, text, additionalText}: Suggestion) => {
            setEditingSuggestion(null);

            const message = additionalText
                ? `${text()}: ${additionalText}`
                : text();

            if (kind === "change") {
                // Change requests should behave like user-sent messages:
                // When the request finishes, the conversation will be updated
                // with the user-sent message and Khanmigo's response, but we
                // need to temporarily update the conversation here to show
                // the user's requested change as an outgoing message. This is
                // already handled in the chat path by `sendChatMessage`, but
                // we need to do it ourselves here:
                GuideStateApi.set("conversation", (conversation) => [
                    ...conversation,
                    {
                        type: "outgoing",
                        text: message,
                        user: "Student",
                        uiOnly: true,
                        serverOnly: false,
                        interactionId: "",
                    },
                ]);

                return requestNewValue(path, message);
            }

            return sendChatMessage(message, {
                containsSpeechToTextContent: false,
                guideVoiceDescriptor: isVoiceOn ? selectedVoice : null,
                requestUIType: "USER_TYPED",
                prepopulatedText: message,
                requestWithTextToSpeech: false,
                timeToSubmit: 0,
            });
        },
        [isVoiceOn, path, requestNewValue, selectedVoice, sendChatMessage],
    );

    const handleItemClick = useCallback(
        (suggestion: Suggestion) => {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideCoeditorToolPopupActionSelected",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigation.id,
                selectedAction: suggestion.action,
            });

            if (suggestion.needsAdditionalInput) {
                // Some suggestions need the user to provide an extra bit
                // of detail! Switch to a text input so the user can add it:
                setEditingSuggestion(suggestion);
            } else {
                // Otherwise, send the message indicated by this suggestion
                // and close the dropdown:
                handleSuggestionAndClose(suggestion);
            }
        },
        [handleSuggestionAndClose, navigation.id, threadId],
    );

    const suggestions = getSuggestionsForPath(data, path);

    return (
        <View
            style={styles.menu}
            ref={menuRef}
            data-ask-khanmigo
            data-remove-on-print
        >
            {editingSuggestion && (
                <AskKhanmigoTextInput
                    path={path}
                    suggestion={editingSuggestion}
                    updateSuggestion={setEditingSuggestion}
                    handleSuggestionClick={handleSuggestionAndClose}
                />
            )}

            {suggestions.map((s, i) => (
                <Tooltip content={s.text()} placement="right" key={i}>
                    <IconButton
                        testId={s.action}
                        style={styles.suggestionIconButton}
                        icon={s.icon}
                        kind="tertiary"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleItemClick(s);
                        }}
                        aria-label={s.text()}
                        aria-haspopup={
                            s.needsAdditionalInput ? "dialog" : undefined
                        }
                    />
                </Tooltip>
            ))}
        </View>
    );
}

const styles = StyleSheet.create({
    menu: {
        position: "absolute",
        willChange: "transform",
        top: "50%",
        transform: "translateY(-50%)",
        insetInlineStart: "100%",
        marginInlineStart: sizing.size_080,
        width: sizing.size_480,
        background: semanticColor.khanmigo.secondary,
        border: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        padding: sizing.size_060,
        boxShadow: `0 8px 8px 0 rgba(0,0,0,0.08),
            0 4px 4px rgba(0,0,0,0.08)`,
        borderRadius: border.radius.radius_040,
        gap: sizing.size_040,
        alignItems: "center",
        ["@container document (max-width: 675px)" as any]: {
            top: `calc(-1 * ${sizing.size_080})`,
            insetInlineEnd: 0,
            transform: "translateY(-100%)",
            insetInlineStart: "unset",
            flexDirection: "row",
            width: "unset",
            boxShadow: "0 2px 4px #ccc",
        },
    },
    suggestionIconButton: {
        color: semanticColor.khanmigo.primary,
        ":hover": {
            background: semanticColor.core.background.instructive.subtle,
        },
    },
});
